import { useCallback, useEffect, useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { getPlaysoutAPI } from '../utils/playsoutApi';
import { getApiGameId } from '../data/games';

/**
 * Playsout API Hook
 * 提供与 Playsout API 交互的便捷方法
 */
export function usePlaysoutAPI() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const api = getPlaysoutAPI();

  const [isLoggedIn, setIsLoggedIn] = useState(() => api.isLoggedIn());
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(() => api.getInviteCode());
  const [error, setError] = useState<string | null>(null);

  // 首次加载时优先从本地 JWT 恢复状态（不依赖钱包连接状态）
  useEffect(() => {
    setIsLoggedIn(api.isLoggedIn());
    setInviteCode(api.getInviteCode());
  }, [api]);

  // 监听钱包地址变化
  useEffect(() => {
    if (address) {
      // 地址变化时不主动清 JWT，仅同步当前会话状态
      setIsLoggedIn(api.isLoggedIn());
      setInviteCode(api.getInviteCode());
    } else {
      // 钱包断开时仍保留 JWT 会话态（仅主动 logout 清理）
      setIsLoggedIn(api.isLoggedIn());
      setInviteCode(api.getInviteCode());
    }
  }, [address, api]);

  // 页面刷新后尝试恢复登录态
  useEffect(() => {
    if (!isConnected || !address) return;
    if (api.ensureToken() && !isLoggedIn) {
      setIsLoggedIn(true);
      setInviteCode(api.getInviteCode());
      console.log('[usePlaysoutAPI] Restored login state from token');
    }
  }, [isConnected, address, api, isLoggedIn]);

  const login = useCallback(async (inputInviteCode?: string): Promise<boolean> => {
    if (api.isLoggedIn()) {
      setIsLoggedIn(true);
      setInviteCode(api.getInviteCode());
      return true;
    }

    if (!address || !isConnected) {
      setError('Wallet not connected');
      return false;
    }

    setIsLoggingIn(true);
    setError(null);
    console.log('[usePlaysoutAPI] Starting Playsout login');

    try {
      let currentInviteCode = inputInviteCode;
      if (!currentInviteCode && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        currentInviteCode = urlParams.get('invite') || undefined;
      }

      await api.login(
        address,
        async (message: string) => {
          return signMessageAsync({ account: address, message });
        },
        currentInviteCode
      );

      const code = await api.fetchInviteCode();
      if (code) {
        setInviteCode(code);
      }
      setIsLoggedIn(api.isLoggedIn());
      setError(null);
      console.log('[usePlaysoutAPI] Playsout login completed');

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      setIsLoggedIn(false);
      console.error('[usePlaysoutAPI] Login error:', err);
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  }, [address, isConnected, signMessageAsync, api]);

  const ensureAuth = useCallback(async (): Promise<boolean> => {
    if (!address || !isConnected) {
      setIsLoggedIn(false);
      return false;
    }

    if (api.isLoggedIn()) {
      if (!isLoggedIn) {
        setIsLoggedIn(true);
      }
      return true;
    }

    const ok = await login();
    if (!ok) {
      setIsLoggedIn(false);
    }
    return ok;
  }, [address, isConnected, api, isLoggedIn, login]);

  // 钱包连接后自动补齐 Playsout JWT
  useEffect(() => {
    if (!isConnected || !address) return;
    if (api.isLoggedIn()) return;

    let cancelled = false;
    console.log('[usePlaysoutAPI] Auto login triggered after wallet connection');
    (async () => {
      const ok = await login();
      if (!cancelled && !ok) {
        setIsLoggedIn(false);
        console.warn('[usePlaysoutAPI] Auto login failed');
      }
      if (!cancelled && ok) {
        console.log('[usePlaysoutAPI] Auto login success');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isConnected, address, api, login]);

  const sendHeartbeat = useCallback(async (gameId: string): Promise<boolean> => {
    // 心跳只依赖本地 JWT，token 校验由 API 层处理
    const currentToken = api.getToken();
    const tokenPreview = currentToken ? `${currentToken.slice(0, 8)}...` : null;
    console.log('[usePlaysoutAPI][Heartbeat] sendHeartbeat called', {
      gameId,
      isConnected,
      address: address ? `${address.slice(0, 6)}...${address.slice(-4)}` : null,
      hasTokenInMemory: !!currentToken,
      tokenPreview,
      isLoggedInState: isLoggedIn,
    });

    const ok = await api.sendHeartbeat(gameId);
    console.log('[usePlaysoutAPI][Heartbeat] sendHeartbeat result', { gameId, ok });
    setIsLoggedIn(api.isLoggedIn());
    return ok;
  }, [api, isConnected, address, isLoggedIn]);

  const fetchInviteCode = useCallback(async (): Promise<string | null> => {
    const authed = await ensureAuth();
    if (!authed) {
      console.warn('[usePlaysoutAPI] Cannot fetch invite code: auth not ready');
      return null;
    }

    let code = await api.fetchInviteCode();
    if (!code && !api.isLoggedIn()) {
      const reloginOk = await login();
      if (reloginOk) {
        code = await api.fetchInviteCode();
      }
    }

    if (code) {
      setInviteCode(code);
    }
    setIsLoggedIn(api.isLoggedIn());
    return code;
  }, [ensureAuth, api, login]);

  const reportBuy = useCallback(async (gameId: string, txHash: string): Promise<boolean> => {
    const authed = await ensureAuth();
    if (!authed) {
      console.warn('[usePlaysoutAPI] Cannot report buy: auth not ready');
      return false;
    }

    let ok = await api.reportBuy(gameId, txHash);
    if (!ok && !api.isLoggedIn()) {
      const reloginOk = await login();
      if (reloginOk) {
        ok = await api.reportBuy(gameId, txHash);
      }
    }

    setIsLoggedIn(api.isLoggedIn());
    return ok;
  }, [ensureAuth, api, login]);

  const logout = useCallback(() => {
    api.clearSession();
    setIsLoggedIn(false);
    setInviteCode(null);
    setError(null);
  }, [api]);

  return {
    isLoggedIn,
    isLoggingIn,
    inviteCode,
    error,
    token: api.getToken(),
    login,
    logout,
    sendHeartbeat,
    fetchInviteCode,
    reportBuy,
    getApiGameId,
  };
}
